let audioContext = null;
let unlockArmed = false;

/**
 * Returns a running AudioContext, or null when the platform has none.
 *
 * iOS Safari and every in-app WebView start an AudioContext suspended and only
 * let it resume inside a user activation window, so this is safe to call
 * repeatedly and is expected to be called from event handlers.
 */
export function ensureAudio() {
  const AudioCtor = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtor) return null;

  if (!audioContext) {
    try {
      audioContext = new AudioCtor();
    } catch (err) {
      return null;
    }
  }

  if (audioContext.state === "suspended" && audioContext.resume) {
    audioContext.resume().catch(() => {});
  }

  return audioContext;
}

/**
 * Arms the audio unlock on the first interaction of any kind.
 *
 * The viewfinder starts without a tap, so there is no gesture of our own to
 * hang the unlock on. Older iOS also needs a silent buffer played once before
 * the context produces sound at all.
 */
export function armAudioUnlock() {
  if (unlockArmed) return;
  unlockArmed = true;

  const unlock = () => {
    const context = ensureAudio();
    if (!context) return;
    try {
      const source = context.createBufferSource();
      source.buffer = context.createBuffer(1, 1, 22050);
      source.connect(context.destination);
      if (source.start) source.start(0);
    } catch (err) {
      // A failed warm-up is not worth reporting: beep() degrades to silence.
    }
  };

  ["pointerdown", "touchend", "keydown"].forEach((type) =>
    document.addEventListener(type, unlock, { once: true, passive: true })
  );
}

/** Greyscales the whole canvas in place. */
export function monochromize(canvas) {
  const context = canvas.getContext("2d", { willReadFrequently: true });
  const image = context.getImageData(0, 0, canvas.width, canvas.height);
  const pixels = image.data;

  for (let i = 0; i < pixels.length; i += 4) {
    const gray =
      pixels[i] * 0.3 + pixels[i + 1] * 0.59 + pixels[i + 2] * 0.11;
    pixels[i] = gray;
    pixels[i + 1] = gray;
    pixels[i + 2] = gray;
  }

  context.putImageData(image, 0, 0);
}

export function beep(freq = 750, duration = 150, vol = 5) {
  const context = ensureAudio();
  if (!context) return;

  try {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.connect(gain);
    oscillator.frequency.value = freq;
    oscillator.type = "square";
    gain.connect(context.destination);
    gain.gain.value = vol * 0.01;
    oscillator.start(context.currentTime);
    oscillator.stop(context.currentTime + duration * 0.001);
  } catch (err) {
    console.warn("[qrcode-scan] could not play the capture tone", err);
  }
}

/**
 * Haptic confirmation. Wallet browsers are often opened with the device muted,
 * where vibration is the only feedback that a code was read.
 */
export function vibrate(pattern = 60) {
  if (typeof navigator.vibrate !== "function") return;

  // Without a prior interaction the call is refused and the browser logs an
  // error of its own, which no try/catch here can suppress.
  const activation = navigator.userActivation;
  if (activation && activation.hasBeenActive === false) return;

  try {
    navigator.vibrate(pattern);
  } catch (err) {
    // Ignored: haptics are an enhancement.
  }
}
